import type { BubbleKind } from "./pose";
import type { CaptionOptions } from "./theme";
import {
  bindTrailingPunctuation,
  captionTokens,
  measureBubbleTextWidth,
} from "./textMeasure";

export const GROW_MS = 280;
export const BUBBLE_FONT_SIZE = 22;
export const BUBBLE_FONT_SIZE_MIN = 10;
export const BUBBLE_LINE_HEIGHT = 1.2;
/** Matches @dwim/ui-tokens typography.fontFamily for live captions. */
export const BUBBLE_FONT_FAMILY = "Inter, system-ui, sans-serif";
/** Single family name for static SVG raster export (resvg). */
export const BUBBLE_EXPORT_FONT_FAMILY = "Inter";
/** Average glyph width as a fraction of font size (system-ui, mixed case). */
const CHAR_EM = 0.46;
const MAX_LINES = 3;

/** Per-side margin from the speak-bubble outer box to the caption safe area. */
export const SPEAK_BUBBLE_BORDER = 8;
/** Extra per-side inset so glyphs stay clear of rounded corners and stroke. */
export const SPEAK_CAPTION_INSET = 4;

export function speakCaptionInnerSize(boxW: number, boxH: number): { width: number; height: number } {
  const margin = SPEAK_BUBBLE_BORDER + SPEAK_CAPTION_INSET;
  return {
    width: Math.max(0, boxW - margin * 2),
    height: Math.max(0, boxH - margin * 2),
  };
}

export type BubblePhase = "idle" | "grow" | "hold" | "shrink";

export type BubbleState = {
  kind: BubbleKind;
  text: string;
  progress: number;
  phase: BubblePhase;
  phaseAt: number;
  holdMs: number;
  fromProgress: number;
  fill?: string;
  tone?: CaptionOptions["tone"];
};

export function maxCharsForWidth(
  innerWidth: number,
  fontSize = BUBBLE_FONT_SIZE,
  charEm = CHAR_EM,
): number {
  return Math.max(4, Math.floor(innerWidth / (fontSize * charEm)));
}

export function wrapBubbleText(text: string, maxChars = 8): string[] {
  return wrapBubbleLayout(text, maxChars).lines;
}

export function wrapBubbleLayout(
  text: string,
  maxChars = 8,
): { lines: string[]; truncated: boolean } {
  const tokens: string[] = [];
  for (const word of text.trim().split(/\s+/).filter((w) => w.length > 0)) {
    if (word.length <= maxChars) {
      tokens.push(word);
    } else {
      for (let i = 0; i < word.length; i += maxChars) {
        tokens.push(word.slice(i, i + maxChars));
      }
    }
  }

  const lines: string[] = [];
  let current = "";
  let overflow = false;

  const flush = (): boolean => {
    if (!current) return false;
    lines.push(current);
    current = "";
    return lines.length >= MAX_LINES;
  };

  for (const token of tokens) {
    if (overflow) break;
    const candidate = current.length === 0 ? token : `${current} ${token}`;
    if (candidate.length <= maxChars) {
      current = candidate;
      continue;
    }
    if (flush()) {
      overflow = true;
      break;
    }
    current = token;
  }

  if (!overflow && current) {
    if (lines.length >= MAX_LINES) {
      overflow = true;
    } else {
      lines.push(current);
    }
  }

  if (overflow && lines.length > 0) {
    const last = lines[lines.length - 1];
    lines[lines.length - 1] = `${last.slice(0, maxChars - 1)}…`;
  }

  return { lines: lines.slice(0, MAX_LINES), truncated: overflow };
}

function splitTokenToWidth(token: string, maxWidth: number, fontSize: number): string[] {
  if (measureBubbleTextWidth(token, fontSize) <= maxWidth || token.includes("\u202F")) {
    return [token];
  }
  const parts: string[] = [];
  let start = 0;
  while (start < token.length) {
    let end = start + 1;
    while (end <= token.length && measureBubbleTextWidth(token.slice(start, end), fontSize) <= maxWidth) {
      end += 1;
    }
    if (end - 1 <= start) {
      // Not one character of this token fits, so the inner loop above never
      // advanced `end` and it is still `start + 1`. The previous guard set `end`
      // to the value it already had, so `start` did not move, `parts` grew by an
      // empty string on every pass, and the loop ended in
      // "RangeError: Invalid array length" once the array passed 2^32-1 entries.
      //
      // Emitting the character whole is the only way to make progress. It
      // overflows maxWidth, which is unavoidable when a single glyph is wider
      // than the bubble; the caller's line loop and truncation still apply.
      end = start + 2;
    }
    parts.push(token.slice(start, end - 1));
    start = end - 1;
  }
  return parts;
}

function wrapBubbleLayoutByWidth(
  text: string,
  maxWidth: number,
  fontSize: number,
): { lines: string[]; truncated: boolean } {
  const tokens: string[] = [];
  for (const token of captionTokens(text)) {
    tokens.push(...splitTokenToWidth(token, maxWidth, fontSize));
  }

  const lines: string[] = [];
  let current = "";
  let overflow = false;

  const flush = (): boolean => {
    if (!current) return false;
    lines.push(current);
    current = "";
    return lines.length >= MAX_LINES;
  };

  for (const token of tokens) {
    if (overflow) break;
    const candidate = current.length === 0 ? token : `${current} ${token}`;
    if (measureBubbleTextWidth(candidate, fontSize) <= maxWidth) {
      current = candidate;
      continue;
    }
    if (flush()) {
      overflow = true;
      break;
    }
    current = token;
  }

  if (!overflow && current) {
    if (lines.length >= MAX_LINES) {
      overflow = true;
    } else {
      lines.push(current);
    }
  }

  if (overflow && lines.length > 0) {
    const last = lines[lines.length - 1];
    let trimmed = last;
    while (trimmed.length > 1 && measureBubbleTextWidth(`${trimmed}…`, fontSize) > maxWidth) {
      trimmed = trimmed.slice(0, -1);
    }
    lines[lines.length - 1] = `${trimmed}…`;
  }

  return { lines: lines.slice(0, MAX_LINES), truncated: overflow };
}

function normalizeCaptionText(value: string): string {
  return value.replace(/[\u202F\s]+/g, " ").trim();
}

function captionLinesMatchSource(lines: string[], source: string): boolean {
  return normalizeCaptionText(lines.join(" ")) === normalizeCaptionText(source);
}

function fitBubbleTextByWidth(
  text: string,
  innerWidth: number,
  innerHeight: number,
  maxFont = BUBBLE_FONT_SIZE,
  minFont = BUBBLE_FONT_SIZE_MIN,
  compact = false,
): { lines: string[]; fontSize: number } {
  const trimmed = text.trim();
  if (!trimmed) return { lines: [], fontSize: maxFont };

  let best: { lines: string[]; fontSize: number } | null = null;
  for (let fontSize = maxFont; fontSize >= minFont; fontSize -= 1) {
    const layout = wrapBubbleLayoutByWidth(trimmed, innerWidth, fontSize);
    const blockH = layout.lines.length * fontSize * BUBBLE_LINE_HEIGHT;
    if (layout.truncated || blockH > innerHeight) {
      continue;
    }
    if (!compact) {
      return { lines: layout.lines, fontSize };
    }
    if (
      !best ||
      layout.lines.length < best.lines.length ||
      (layout.lines.length === best.lines.length && fontSize > best.fontSize)
    ) {
      best = { lines: layout.lines, fontSize };
    }
  }
  return best ?? { lines: [trimmed], fontSize: minFont };
}

export function fitBubbleText(
  text: string,
  innerWidth: number,
  innerHeight: number,
  maxFont = BUBBLE_FONT_SIZE,
  minFont = BUBBLE_FONT_SIZE_MIN,
  layout: "standard" | "compact" = "standard",
  charEm = CHAR_EM,
): { lines: string[]; fontSize: number } {
  const trimmed = text.trim();
  if (!trimmed) return { lines: [], fontSize: maxFont };

  if (layout === "compact") {
    let best: { lines: string[]; fontSize: number; truncated: boolean } | null = null;
    for (let fontSize = maxFont; fontSize >= minFont; fontSize -= 1) {
      const maxChars = maxCharsForWidth(innerWidth, fontSize, charEm);
      const layout = wrapBubbleLayout(trimmed, maxChars);
      const blockH = layout.lines.length * fontSize * BUBBLE_LINE_HEIGHT;
      if (!layout.truncated && blockH <= innerHeight) {
        if (
          !best ||
          layout.lines.length < best.lines.length ||
          (layout.lines.length === best.lines.length && fontSize > best.fontSize)
        ) {
          best = { ...layout, fontSize };
        }
      }
    }
    return { lines: best?.lines ?? [trimmed], fontSize: best?.fontSize ?? minFont };
  }

  let fontSize = maxFont;
  let best: { lines: string[]; fontSize: number; truncated: boolean } | null = null;
  while (fontSize >= minFont) {
    const maxChars = maxCharsForWidth(innerWidth, fontSize, charEm);
    const layout = wrapBubbleLayout(trimmed, maxChars);
    const blockH = layout.lines.length * fontSize * BUBBLE_LINE_HEIGHT;
    best = { ...layout, fontSize };
    if (!layout.truncated && blockH <= innerHeight) {
      return { lines: layout.lines, fontSize };
    }
    fontSize -= 1;
  }
  return { lines: best?.lines ?? [trimmed], fontSize: best?.fontSize ?? minFont };
}

function fitSpeakBubbleText(
  text: string,
  innerWidth: number,
  innerHeight: number,
  layout: "standard" | "compact",
): { lines: string[]; fontSize: number } {
  const prepared = bindTrailingPunctuation(text);
  return fitBubbleTextByWidth(prepared, innerWidth, innerHeight, undefined, undefined, layout === "compact");
}

function speakBubbleTextFits(
  text: string,
  innerWidth: number,
  innerHeight: number,
  layout: "standard" | "compact",
): boolean {
  const trimmed = text.trim();
  if (!trimmed) {
    return true;
  }
  const { lines, fontSize } = fitSpeakBubbleText(trimmed, innerWidth, innerHeight, layout);
  if (lines.some((line) => line.endsWith("…"))) {
    return false;
  }
  if (!captionLinesMatchSource(lines, trimmed)) {
    return false;
  }
  return lines.every((line) => measureBubbleTextWidth(line, fontSize) <= innerWidth + 0.5);
}

/** Pick standard vs compact for a fixed speak-bubble inner box (max readable size, no truncation). */
export function speakCaptionLayout(
  text: string,
  innerWidth: number,
  innerHeight: number,
): "standard" | "compact" {
  let best: { fontSize: number; layout: "standard" | "compact" } | null = null;
  for (const layout of ["standard", "compact"] as const) {
    if (!speakBubbleTextFits(text, innerWidth, innerHeight, layout)) {
      continue;
    }
    const { fontSize } = fitSpeakBubbleText(text, innerWidth, innerHeight, layout);
    if (
      !best ||
      fontSize > best.fontSize ||
      (fontSize === best.fontSize && layout === "standard" && best.layout === "compact")
    ) {
      best = { fontSize, layout };
    }
  }
  return best?.layout ?? "compact";
}

export { bindTrailingPunctuation, measureBubbleTextWidth } from "./textMeasure";

export function fitSpeakCaption(
  text: string,
  innerWidth: number,
  innerHeight: number,
  layout: "standard" | "compact" = speakCaptionLayout(text, innerWidth, innerHeight),
): { lines: string[]; fontSize: number } {
  return fitSpeakBubbleText(text, innerWidth, innerHeight, layout);
}

/** True when the full string fits without ellipsis truncation. */
export function bubbleTextFits(
  text: string,
  innerWidth: number,
  innerHeight: number,
  layout: "standard" | "compact" = "standard",
): boolean {
  const trimmed = text.trim();
  if (!trimmed) {
    return true;
  }
  const { lines } = fitBubbleText(trimmed, innerWidth, innerHeight, undefined, undefined, layout);
  if (lines.some((line) => line.endsWith("…"))) {
    return false;
  }
  const normalize = (value: string) => value.replace(/\s+/g, " ").trim();
  return normalize(lines.join(" ")) === normalize(trimmed);
}

export function bubbleHoldMs(text: string): number {
  return Math.max(1800, 50 * text.length);
}

export function createBubbleState(): BubbleState {
  return {
    kind: "none",
    text: "",
    progress: 0,
    phase: "idle",
    phaseAt: 0,
    holdMs: 0,
    fromProgress: 0,
    fill: undefined,
    tone: "default",
  };
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function invertEaseOutCubic(progress: number): number {
  const p = Math.min(1, Math.max(0, progress));
  return 1 - Math.cbrt(1 - p);
}

function beginCaption(
  state: BubbleState,
  kind: Exclude<BubbleKind, "none">,
  text: string,
  nowMs: number,
  reducedMotion: boolean,
  options?: CaptionOptions,
): BubbleState {
  const trimmed = text.trim();
  if (trimmed === "") return state;

  let phase: BubblePhase;
  let progress: number;
  if (reducedMotion) {
    phase = "hold";
    progress = 1;
  } else if (state.progress > 0) {
    progress = state.progress;
    phase = progress < 1 ? "grow" : "hold";
  } else {
    phase = "grow";
    progress = 0;
  }

  const phaseAt =
    phase === "grow" && progress > 0 && progress < 1
      ? nowMs - invertEaseOutCubic(progress) * GROW_MS
      : nowMs;

  return {
    ...state,
    kind,
    text: trimmed,
    holdMs: options?.holdMs !== undefined ? Math.max(0, options.holdMs) : bubbleHoldMs(trimmed),
    phase,
    progress,
    phaseAt,
    fill: options?.fill,
    tone: options?.tone ?? "default",
  };
}

export function startSpeak(
  state: BubbleState,
  text: string,
  nowMs: number,
  reducedMotion: boolean,
  options?: CaptionOptions,
): BubbleState {
  return beginCaption(state, "speak", text, nowMs, reducedMotion, options);
}

export function startThink(
  state: BubbleState,
  text: string,
  nowMs: number,
  reducedMotion: boolean,
  options?: CaptionOptions,
): BubbleState {
  return beginCaption(state, "think", text, nowMs, reducedMotion, options);
}

function idleState(phaseAt: number): BubbleState {
  return {
    kind: "none",
    text: "",
    progress: 0,
    phase: "idle",
    phaseAt,
    holdMs: 0,
    fromProgress: 0,
    fill: undefined,
    tone: "default",
  };
}

function beginShrink(state: BubbleState, nowMs: number): BubbleState {
  return {
    ...state,
    phase: "shrink",
    phaseAt: nowMs,
    fromProgress: state.progress,
  };
}

export function startSilence(
  state: BubbleState,
  nowMs: number,
  reducedMotion = false,
): BubbleState {
  if (state.phase === "idle") return state;
  if (reducedMotion) return idleState(nowMs);
  return beginShrink(state, nowMs);
}

export function tickBubble(
  state: BubbleState,
  nowMs: number,
  reducedMotion: boolean,
): BubbleState {
  switch (state.phase) {
    case "idle":
      return state;
    case "grow": {
      if (reducedMotion) {
        return { ...state, phase: "hold", progress: 1, phaseAt: nowMs };
      }
      const t = Math.min(1, (nowMs - state.phaseAt) / GROW_MS);
      const progress = easeOutCubic(t);
      if (t >= 1) {
        return { ...state, phase: "hold", progress: 1, phaseAt: nowMs };
      }
      return { ...state, progress };
    }
    case "hold": {
      if (state.holdMs > 0 && nowMs - state.phaseAt >= state.holdMs) {
        return beginShrink(state, nowMs);
      }
      return state;
    }
    case "shrink": {
      if (reducedMotion) {
        return idleState(nowMs);
      }
      const t = Math.min(1, (nowMs - state.phaseAt) / GROW_MS);
      const progress = state.fromProgress * (1 - t);
      if (t >= 1) {
        return idleState(nowMs);
      }
      return { ...state, progress };
    }
  }
}
